import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { tvShows, categories } from '@/mocks/tvShows';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import TVShowCard from '@/components/base/TVShowCard';

const hotShows = tvShows.filter((s) => s.isHot).slice(0, 5);
const newShows = tvShows.filter((s) => s.isNew).slice(0, 8);
const allShowsExcludingHot = tvShows.filter((s) => !s.isHot);

function HeroCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const heroShows = hotShows.slice(0, 3);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % heroShows.length);
  }, [heroShows.length]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + heroShows.length) % heroShows.length);
  }, [heroShows.length]);

  useEffect(() => {
    const timer = setInterval(nextSlide, 5000);
    return () => clearInterval(timer);
  }, [nextSlide]);

  const currentShow = heroShows[currentIndex];

  return (
    <section className="relative w-full h-[420px] md:h-[560px] overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={currentShow.image}
          alt={currentShow.title}
          className="w-full h-full object-cover transition-all duration-700"
          key={currentShow.id}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60"></div>
      </div>

      <div className="absolute inset-0 flex items-center">
        <div className="w-full px-4 md:px-6">
          <div className="max-w-7xl mx-auto w-full">
            <div className="max-w-xl">
              <div className="flex items-center gap-2 mb-4">
                <span className="px-3 py-1 bg-primary-500 text-white text-xs font-medium rounded-md whitespace-nowrap">
                  {currentShow.status}
                </span>
                <span className="px-3 py-1 bg-white/20 text-white text-xs font-medium rounded-md whitespace-nowrap">
                  {currentShow.category}
                </span>
                <span className="text-white/80 text-xs">{currentShow.year}</span>
              </div>
              <h2 className="font-heading text-3xl md:text-5xl font-bold text-white mb-4">
                {currentShow.title}
              </h2>
              <p className="text-white/70 text-sm md:text-base leading-relaxed line-clamp-2 mb-6">
                {currentShow.description}
              </p>
              <div className="flex items-center gap-4 mb-6">
                <span className="flex items-center gap-1.5 text-accent-400">
                  <i className="ri-star-fill"></i>
                  <span className="text-lg font-semibold">{currentShow.rating}</span>
                </span>
                <span className="text-white/60 text-sm">
                  主演: {currentShow.actors.slice(0, 2).join('、')}
                </span>
              </div>
              <Link
                to={`/article/${currentShow.id}`}
                className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-circle-line"></i>
                立即观看
              </Link>
            </div>
          </div>
        </div>
      </div>

      <button
        onClick={prevSlide}
        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer"
        aria-label="上一部"
      >
        <i className="ri-arrow-left-s-line text-xl"></i>
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 w-10 h-10 md:w-12 md:h-12 flex items-center justify-center bg-white/10 hover:bg-white/20 rounded-full text-white transition-colors cursor-pointer"
        aria-label="下一部"
      >
        <i className="ri-arrow-right-s-line text-xl"></i>
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex items-center gap-2">
        {heroShows.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentIndex(index)}
            className={`w-1.5 h-1.5 rounded-full transition-all cursor-pointer ${
              index === currentIndex ? 'w-6 bg-white' : 'bg-white/40'
            }`}
            aria-label={`切换到第${index + 1}部`}
          />
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const websiteSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '被窝网电影',
    url: 'https://www.beiwodianying.com/',
    description: '被窝网电影提供电视剧全集免费高清在线观看，流畅不缓冲，聚合热门电视剧资源',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.beiwodianying.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const orgSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '被窝网电影',
    url: 'https://www.beiwodianying.com/',
    description: '免费高清电视剧在线观看平台',
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(websiteSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(orgSchema)}</script>
      <main>
        <Navbar />
        <HeroCarousel />

        {/* Hot Shows Section */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto w-full">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  热门推荐
                </h2>
              </div>
              <Link
                to="/category/guochanju"
                className="text-sm text-foreground-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                查看更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-5">
              {hotShows.map((show) => (
                <TVShowCard key={show.id} show={show} />
              ))}
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="w-full px-4 md:px-6 py-10 bg-background-100">
          <div className="max-w-7xl mx-auto w-full">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                影视分类
              </h2>
            </div>
            <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-6 gap-3 md:gap-4">
              {categories.map((cat) => {
                const count = tvShows.filter((s) => s.category === cat.name).length;
                return (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="flex flex-col items-center gap-3 p-4 md:p-6 bg-background-50 rounded-xl hover:bg-background-50/80 transition-all hover:-translate-y-1 cursor-pointer"
                  >
                    <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-primary-100 text-primary-600 rounded-xl">
                      <i className={`${cat.icon} text-xl md:text-2xl`}></i>
                    </div>
                    <span className="font-heading text-sm font-medium text-foreground-800 whitespace-nowrap">
                      {cat.name}
                    </span>
                    <span className="text-xs text-foreground-400">{count}部</span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* New Shows */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-7xl mx-auto w-full">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <div className="w-1 h-6 bg-accent-500 rounded-full"></div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                  最新上线
                </h2>
              </div>
              <Link
                to="/category/guochanju"
                className="text-sm text-foreground-500 hover:text-primary-600 transition-colors flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                查看更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {newShows.map((show) => (
                <TVShowCard key={show.id} show={show} />
              ))}
            </div>
          </div>
        </section>

        {/* All Shows by Category Preview */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-100">
          <div className="max-w-7xl mx-auto w-full">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                全部剧集
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {allShowsExcludingHot.slice(0, 12).map((show) => (
                <TVShowCard key={show.id} show={show} />
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="w-full px-4 md:px-6 py-10 md:py-14 bg-background-50">
          <div className="max-w-3xl mx-auto w-full">
            <div className="flex items-center gap-3 justify-center mb-8">
              <div className="w-1 h-6 bg-primary-500 rounded-full"></div>
              <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
                常见问题
              </h2>
            </div>
            <div className="space-y-3">
              <details className="group bg-background-100 rounded-lg p-5 cursor-pointer">
                <summary className="font-heading font-semibold text-foreground-800 text-sm md:text-base list-none flex items-center justify-between">
                  被窝网电影需要付费吗？
                  <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-foreground-500"></i>
                </summary>
                <p className="mt-3 text-foreground-600 text-sm leading-relaxed">
                  完全不需要！被窝网电影所有电视剧资源均为免费提供，无需任何充值或会员，打开即可观看全集。
                </p>
              </details>
              <details className="group bg-background-100 rounded-lg p-5 cursor-pointer">
                <summary className="font-heading font-semibold text-foreground-800 text-sm md:text-base list-none flex items-center justify-between">
                  观看视频会缓冲卡顿吗？
                  <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-foreground-500"></i>
                </summary>
                <p className="mt-3 text-foreground-600 text-sm leading-relaxed">
                  我们采用了先进的CDN加速技术，确保高清视频流畅播放不缓冲。只需稳定的网络连接，即可享受流畅的追剧体验。
                </p>
              </details>
              <details className="group bg-background-100 rounded-lg p-5 cursor-pointer">
                <summary className="font-heading font-semibold text-foreground-800 text-sm md:text-base list-none flex items-center justify-between">
                  被窝网电影有哪些电视剧资源？
                  <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-foreground-500"></i>
                </summary>
                <p className="mt-3 text-foreground-600 text-sm leading-relaxed">
                  我们聚合了丰富的电视剧资源，涵盖国产剧、韩剧、美剧、日剧、动漫、综艺等多个分类。从最新热播剧到经典老剧，应有尽有，每日更新。
                </p>
              </details>
              <details className="group bg-background-100 rounded-lg p-5 cursor-pointer">
                <summary className="font-heading font-semibold text-foreground-800 text-sm md:text-base list-none flex items-center justify-between">
                  支持哪些设备观看？
                  <i className="ri-arrow-down-s-line group-open:rotate-180 transition-transform text-foreground-500"></i>
                </summary>
                <p className="mt-3 text-foreground-600 text-sm leading-relaxed">
                  被窝网电影支持全平台观看，包括电脑、平板和手机。只需打开浏览器访问我们的网站，即可随时随地开始追剧。
                </p>
              </details>
            </div>
          </div>
        </section>

        <Footer />
      </main>
    </>
  );
}